import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const NotificationsContext = createContext(null);

// VAPID key is base64url; the browser needs a Uint8Array.
function urlB64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const [unreadChat, setUnreadChat] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);

  const seenAnnouncements = useRef(null);
  const seenLastMsg = useRef({});
  const subs = useRef(new Set());
  const focusedConv = useRef(null);
  const wsRef = useRef(null);
  const retry = useRef(1000);
  const started = useRef(false);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return "denied";
    const p = await Notification.requestPermission();
    setPermission(p);
    return p;
  }, []);

  const notify = useCallback((title, body, opts = {}) => {
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      const payload = { title, body, url: opts.url || "/", tag: opts.tag, icon: opts.icon || "/icon-192.png" };
      // Prefer the service worker so notifications are persistent, clickable
      // (deep-link) and can render while the tab is backgrounded.
      if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.ready
          .then((reg) => reg.showNotification(title, {
            body, icon: payload.icon, badge: "/icon-192.png",
            tag: payload.tag || undefined, renotify: !!payload.tag,
            vibrate: [80, 40, 80], data: { url: payload.url },
          }))
          .catch(() => { try { new Notification(title, { body, icon: payload.icon }); } catch (e) { console.debug(e); } });
      } else {
        try { new Notification(title, { body, icon: payload.icon }); }
        catch (e) { console.debug("Desktop notification unavailable:", e); }
      }
    }
    toast(title, { description: body });
  }, []);

  // Background Web Push subscription. Gracefully no-ops until the backend
  // exposes /push/vapid-public-key + /push/subscribe (see WEB_PUSH_SPEC.md).
  useEffect(() => {
    if (!user || permission !== "granted") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/push/vapid-public-key");
        const key = data && data.public_key;
        if (!key || cancelled) return;
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription()
          || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(key) });
        if (cancelled) return;
        await api.post("/push/subscribe", sub.toJSON());
      } catch (e) { console.debug("Web push not active yet:", e?.response?.status || e?.message); }
    })();
    return () => { cancelled = true; };
  }, [user, permission]);

  const dispatch = useCallback((evt) => {
    subs.current.forEach((fn) => { try { fn(evt); } catch (e) { console.debug(e); } });
  }, []);

  const subscribe = useCallback((fn) => {
    subs.current.add(fn);
    return () => subs.current.delete(fn);
  }, []);

  const sendWs = useCallback((obj) => {
    try { if (wsRef.current && wsRef.current.readyState === 1) wsRef.current.send(JSON.stringify(obj)); }
    catch (e) { console.debug(e); }
  }, []);

  const focusConversation = useCallback((cid) => { focusedConv.current = cid; sendWs({ type: "chat.focus", conv_id: cid }); }, [sendWs]);
  const blurConversation = useCallback(() => { focusedConv.current = null; sendWs({ type: "chat.blur" }); }, [sendWs]);
  const sendTyping = useCallback((cid) => sendWs({ type: "chat.typing", conv_id: cid }), [sendWs]);

  const refreshConvos = useCallback(async () => {
    try {
      const { data: convos } = await api.get("/conversations");
      let unread = 0;
      convos.forEach((c) => { unread += c.unread_count || 0; });
      setUnreadChat(unread);
      return convos;
    } catch (e) { return null; }
  }, []);

  const handleEvent = useCallback((evt) => {
    switch (evt.type) {
      case "message.new": {
        const m = evt.message || {};
        const cid = evt.conversation_id;
        if (m.sender_id && user && m.sender_id !== user.id) {
          seenLastMsg.current[cid] = m.created_at;
          if (focusedConv.current !== cid) {
            notify(`New message from ${m.sender_name || "Team"}`, m.text || "📎 Photo", { url: "/chat", tag: `chat-${cid}` });
          }
        }
        refreshConvos();
        break;
      }
      case "announcement.new": {
        const a = evt.announcement || {};
        if (seenAnnouncements.current) seenAnnouncements.current.add(a.id);
        notify(`Announcement: ${a.title}`, a.body, { url: "/announcements", tag: `ann-${a.id}` });
        break;
      }
      case "conversation.new":
      case "conversation.deleted":
      case "announcement.deleted":
        refreshConvos();
        break;
      case "call.new": {
        const c = evt.call || {};
        notify(`New dispatch: ${c.agency_for || "Transport call"}`,
          [c.deceased_name, c.pickup_location && `${c.pickup_location} → ${c.dropoff_location || ""}`].filter(Boolean).join(" · ") || "New transport assigned",
          { url: c.id ? `/transports/${c.id}` : "/transports", tag: `call-${c.id}` });
        break;
      }
      case "time_off.status": {
        const st = evt.status || "updated";
        notify(`Time-off ${st}`, evt.manager_note || `Your time-off request was ${st}.`, { url: "/time-off", tag: `timeoff-${evt.request_id || evt.id || ""}` });
        break;
      }
      case "schedule.uploaded": {
        notify("New schedule posted", evt.title || evt.label || "A new monthly schedule is available.", { url: "/schedules", tag: `sched-${evt.id || ""}` });
        break;
      }
      default:
        break;
    }
    dispatch(evt);
  }, [user, notify, refreshConvos, dispatch]);

  // WebSocket: real-time chat + announcements with exponential-backoff reconnect.
  useEffect(() => {
    if (!user) return;
    let stopped = false;
    const base = process.env.REACT_APP_CURTRON_API || "";

    const scheduleReconnect = () => {
      if (stopped) return;
      const d = retry.current;
      retry.current = Math.min(retry.current * 2, 30000);
      setTimeout(() => { if (!stopped) connect(); }, d);
    };

    const connect = () => {
      const token = localStorage.getItem("curtron_token");
      if (!token || stopped) return;
      const url = base.replace(/^http/, "ws") + "/api/ws?token=" + encodeURIComponent(token);
      let ws;
      try { ws = new WebSocket(url); } catch (e) { scheduleReconnect(); return; }
      wsRef.current = ws;
      ws.onopen = () => {
        if (wsRef.current !== ws) return;
        retry.current = 1000;
        setWsConnected(true);
        if (focusedConv.current) sendWs({ type: "chat.focus", conv_id: focusedConv.current });
      };
      ws.onmessage = (e) => {
        if (wsRef.current !== ws) return;
        let evt; try { evt = JSON.parse(e.data); } catch { return; }
        handleEvent(evt);
      };
      ws.onerror = () => { try { ws.close(); } catch (err) { console.debug(err); } };
      ws.onclose = (ev) => {
        if (wsRef.current === ws) { wsRef.current = null; setWsConnected(false); }
        if (stopped) return;
        if (ev.code === 4401) {
          localStorage.removeItem("curtron_token");
          if (!window.location.pathname.startsWith("/login")) window.location.href = "/login?expired=1";
          return;
        }
        scheduleReconnect();
      };
    };

    connect();
    return () => { stopped = true; try { wsRef.current?.close(); } catch (e) { console.debug(e); } };
  }, [user, handleEvent, sendWs]);

  // Fallback polling (announcements + unread) in case the socket is unavailable.
  useEffect(() => {
    if (!user || started.current) return;
    started.current = true;

    const poll = async () => {
      try {
        const { data: anns } = await api.get("/announcements");
        if (seenAnnouncements.current === null) {
          seenAnnouncements.current = new Set(anns.map((a) => a.id));
        } else {
          anns.forEach((a) => {
            if (!seenAnnouncements.current.has(a.id)) {
              seenAnnouncements.current.add(a.id);
              notify(`Announcement: ${a.title}`, a.body, { url: "/announcements", tag: `ann-${a.id}` });
            }
          });
        }
      } catch (e) { console.debug(e); }

      try {
        const { data: convos } = await api.get("/conversations");
        let unread = 0;
        const first = Object.keys(seenLastMsg.current).length === 0;
        convos.forEach((c) => {
          unread += c.unread_count || 0;
          const lm = c.last_message;
          if (lm && lm.sender_id !== user.id) {
            if (!first && seenLastMsg.current[c.id] && seenLastMsg.current[c.id] !== lm.created_at && focusedConv.current !== c.id) {
              notify(`New message from ${lm.sender_name || "Team"}`, lm.text || "📎 Photo", { url: "/chat", tag: `chat-${c.id}` });
            }
            seenLastMsg.current[c.id] = lm.created_at;
          }
        });
        setUnreadChat(unread);
      } catch (e) { console.debug(e); }
    };

    poll();
    const id = setInterval(poll, 30000);
    return () => clearInterval(id);
  }, [user, notify]);

  return (
    <NotificationsContext.Provider value={{ permission, requestPermission, notify, unreadChat, subscribe, focusConversation, blurConversation, sendTyping, wsConnected }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationsContext);
}
